import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'contexts/AlertContext';
import { useMeetingMembers } from 'hooks/useMeeting';
import { meetingStore } from 'store/meetingStore';
import { SafeReturnRepository } from 'repository/safeReturnRepository';
import { UserHomeRepository } from 'repository/userHomeRepository';
import { setNativeSafeReturnSession, setNativeMeetingSchedule, isNativeApp } from 'common/webview';
import { AppScreen, Button, ButtonFooter, Section, SectionHeader } from 'components/ds';
import styles from './SafeReturnStart.module.css';

type DestMode = 'home' | 'custom';

interface MemberRow {
    userNo: number;
    nickname: string;
    profileImgUrl?: string;
    isMe: boolean;
}

export const SafeReturnStart: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { meetingId } = useParams<{ meetingId: string }>();
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const { data: membersData } = useMeetingMembers();

    const currentUserNo = Number(sessionStorage.getItem('userNo')) || 0;

    const [homeLoaded, setHomeLoaded] = useState(false);
    const [homeAddr, setHomeAddr] = useState<{ address: string; lat: number; lng: number; radiusM: number } | null>(null);

    const [destMode, setDestMode] = useState<DestMode>('home');
    const [customAddr, setCustomAddr] = useState<{ address: string; lat: number; lng: number } | null>(null);
    const [radiusM, setRadiusM] = useState<number>(100);
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (meetingId) meetingStore.onGetMeetingMembers(meetingId);
    }, [meetingId]);

    useEffect(() => {
        UserHomeRepository.onGetHome()
            .then((res: any) => {
                const data = res?.data?.data || res?.data;
                if (data && data.latitude != null && data.longitude != null) {
                    const h = {
                        address: data.address || '',
                        lat: Number(data.latitude),
                        lng: Number(data.longitude),
                        radiusM: Number(data.radiusM) || 100,
                    };
                    setHomeAddr(h);
                    setRadiusM(h.radiusM);
                } else {
                    setDestMode('custom');
                }
            })
            .catch(() => setDestMode('custom'))
            .finally(() => setHomeLoaded(true));
    }, []);

    // PlaceSearch 결과 복귀
    useEffect(() => {
        const placeInfo = (location.state as any)?.placeInfo;
        if (placeInfo && placeInfo.lat && placeInfo.lng) {
            setCustomAddr({
                address: placeInfo.address || placeInfo.name || '',
                lat: Number(placeInfo.lat),
                lng: Number(placeInfo.lng),
            });
            setDestMode('custom');
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const members: MemberRow[] = (membersData?.data || []).map((m: any) => {
        let nickname = m.nickname || '';
        try { nickname = decodeURIComponent(nickname); } catch { /* ignore */ }
        return {
            userNo: Number(m.userNo || m.user_no),
            nickname: nickname || t('safeReturn.fallbackUser', { userNo: m.userNo }),
            profileImgUrl: m.profileImageUrl || m.profileImgPath || undefined,
            isMe: Number(m.userNo || m.user_no) === currentUserNo,
        };
    });
    const others = members.filter(m => !m.isMe && m.userNo > 0);

    const toggleSelect = (userNo: number) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(userNo)) next.delete(userNo);
            else next.add(userNo);
            return next;
        });
    };

    const toggleAll = () => {
        if (selected.size === others.length) setSelected(new Set());
        else setSelected(new Set(others.map(m => m.userNo)));
    };

    const openPlaceSearch = () => {
        navigate('/meeting/place-search', {
            state: {
                returnTo: `/safe-return/start/${meetingId}`,
                placeInfo: customAddr ? { name: customAddr.address, address: customAddr.address, lat: customAddr.lat, lng: customAddr.lng } : undefined,
            },
        });
    };

    const handleSubmit = async () => {
        if (!meetingId) return;
        const dest = destMode === 'home' ? homeAddr : customAddr;
        if (!dest) {
            showAlert({ message: destMode === 'home' ? t('safeReturn.alertNoHome') : t('safeReturn.alertNoCustomDest') });
            return;
        }
        if (selected.size === 0) {
            showAlert({ message: t('safeReturn.alertSelectWatcher') });
            return;
        }

        setSubmitting(true);
        try {
            const res = await SafeReturnRepository.onCreateSession({
                meetingId,
                destLat: dest.lat,
                destLng: dest.lng,
                destAddress: dest.address,
                destRadiusM: radiusM,
                watcherUserNos: Array.from(selected),
            });
            const data = res?.data?.data || res?.data;
            const sessionId = data?.sessionId;
            if (!sessionId) throw new Error('세션 생성 실패');

            // 네이티브 앱: 세션 생성 즉시 BG location 유지 요청 (모임 종료와 무관하게 귀가 동안 추적)
            try {
                const expiresMs = data?.expiresAt
                    ? new Date(data.expiresAt).getTime()
                    : Date.now() + 4 * 60 * 60 * 1000;
                setNativeSafeReturnSession({
                    sessionId: Number(sessionId),
                    travelerUserNo: Number(data?.travelerUserNo) || currentUserNo,
                    destLat: dest.lat,
                    destLng: dest.lng,
                    destRadiusM: Number(data?.destRadiusM) || radiusM,
                    expiresAt: expiresMs,
                    role: 'TRAVELER',
                });
                // 모임 스케줄 endTime 을 safe-return 만료시각까지 연장 → 모임 종료 후에도
                // 기존 BG GPS + LBS 보고 흐름이 자연스럽게 계속 동작 (별도 endpoint 불필요)
                // anchor 좌표는 safe-return 의 목적지 (집/지정 장소)
                if (isNativeApp() && meetingId) {
                    setNativeMeetingSchedule(meetingId, Date.now(), expiresMs, {
                        meetingLat: dest.lat,
                        meetingLon: dest.lng,
                    });
                }
            } catch (bridgeErr) {
                console.warn('[SafeReturnStart] native bridge 호출 실패', bridgeErr);
            }

            navigate(`/safe-return/session/${sessionId}`, { replace: true });
        } catch (e) {
            console.error('[SafeReturnStart] 생성 실패', e);
            showAlert({ message: t('safeReturn.alertCreateFailed') });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AppScreen title={t('safeReturn.startTitle')} bar="sub" hasFooter>
            {/* 어디로 가는지 */}
            <Section spacing="tight">
                <SectionHeader title={t('safeReturn.destination')} />
                <div className={styles.picks}>
                    <button
                        type="button"
                        className={destMode === 'home' ? `${styles.pick} ${styles.pickOn}` : styles.pick}
                        onClick={() => homeAddr && setDestMode('home')}
                        disabled={!homeAddr}
                        aria-pressed={destMode === 'home'}
                    >
                        <span className={styles.pickMark} aria-hidden />
                        <span className={styles.pickBody}>
                            <span className={styles.pickTitle}>{t('safeReturn.useHomeAddress')}</span>
                            <span className={styles.pickSub}>
                                {homeLoaded && homeAddr
                                    ? homeAddr.address
                                    : homeLoaded
                                        ? t('safeReturn.noHomeAddress')
                                        : t('safeReturn.loading')}
                            </span>
                        </span>
                    </button>

                    {homeLoaded && !homeAddr && (
                        <div className={styles.pickAction}>
                            <Button
                                variant="secondary"
                                size="sm"
                                block
                                onClick={() => navigate('/profile/home-address')}
                            >
                                {t('safeReturn.registerHome')}
                            </Button>
                        </div>
                    )}

                    <button
                        type="button"
                        className={destMode === 'custom' ? `${styles.pick} ${styles.pickOn}` : styles.pick}
                        onClick={() => setDestMode('custom')}
                        aria-pressed={destMode === 'custom'}
                    >
                        <span className={styles.pickMark} aria-hidden />
                        <span className={styles.pickBody}>
                            <span className={styles.pickTitle}>{t('safeReturn.customAddress')}</span>
                            <span className={styles.pickSub}>
                                {customAddr ? customAddr.address : t('safeReturn.searchAddressGuide')}
                            </span>
                        </span>
                    </button>

                    {destMode === 'custom' && (
                        <div className={styles.pickAction}>
                            <Button variant="secondary" size="sm" block onClick={openPlaceSearch}>
                                {customAddr ? t('safeReturn.changeAddress') : t('safeReturn.searchAddressBtn')}
                            </Button>
                        </div>
                    )}
                </div>
            </Section>

            {/* 도착 판정 반경 */}
            <Section divided>
                <SectionHeader title={t('safeReturn.arrivalRadius')} />
                <div className={styles.radius}>
                    <input
                        type="range"
                        min={50}
                        max={300}
                        step={10}
                        value={radiusM}
                        onChange={(e) => setRadiusM(Number(e.target.value))}
                        className={styles.slider}
                        aria-label={t('safeReturn.arrivalRadius')}
                    />
                    <span className={styles.radiusValue}>{radiusM}m</span>
                </div>
            </Section>

            {/* 누가 지켜볼지 */}
            <Section divided last>
                <SectionHeader
                    title={t('safeReturn.watchersTitle')}
                    count={selected.size}
                    moreLabel={others.length > 0
                        ? (selected.size === others.length ? t('safeReturn.deselectAll') : t('safeReturn.selectAll'))
                        : undefined}
                    onMore={others.length > 0 ? toggleAll : undefined}
                />
                {others.length === 0 ? (
                    <p className={styles.empty}>{t('safeReturn.noOtherMembers')}</p>
                ) : (
                    <ul className={styles.people}>
                        {others.map(m => {
                            const checked = selected.has(m.userNo);
                            return (
                                <li key={m.userNo}>
                                    <button
                                        type="button"
                                        className={checked ? `${styles.person} ${styles.personOn}` : styles.person}
                                        onClick={() => toggleSelect(m.userNo)}
                                        aria-pressed={checked}
                                    >
                                        <span className={styles.avatar}>
                                            {m.profileImgUrl
                                                ? <img src={m.profileImgUrl} alt="" />
                                                : (m.nickname[0] || '?')}
                                        </span>
                                        <span className={styles.personName}>{m.nickname}</span>
                                        <span className={styles.check} aria-hidden>
                                            {checked && (
                                                <svg viewBox="0 0 24 24" width="14" height="14" fill="none"
                                                    stroke="currentColor" strokeWidth="3"
                                                    strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
                <p className={styles.hint}>{t('safeReturn.hint')}</p>
            </Section>

            <ButtonFooter>
                <Button
                    variant="primary"
                    size="lg"
                    block
                    onClick={handleSubmit}
                    disabled={submitting || selected.size === 0}
                >
                    {submitting ? t('safeReturn.submitting') : t('safeReturn.submit', { count: selected.size })}
                </Button>
            </ButtonFooter>
        </AppScreen>
    );
};

export default SafeReturnStart;
